import { useMutation, useQueryClient } from "@tanstack/react-query";
import { defaultInstance, directApiInstance } from "../api/httpClient";
import { NotificationService } from "../services/notification";

interface InvitationPayload {
  details?: string;
  contact_id?: string;
  estate_id?: string;
  business_id?: string;
  access_card_id?: string;
  created_on_behalf_by?: string;
  created_by?: string;
  modified_by?: string;
  type_id?: string;
  invitation_from?: string;
  invitation_to?: string;
  host_user_id?: string;
  status_id?: string;
  children_count?: string | number;
  qr_id?: string;
  is_analyzed?: boolean;
  main_guest?: {
    name: string;
    last_name: string;
    tel: string;
  };
  companions?: Array<{
    name: string;
    last_name: string;
  }>;
}

interface BusinessAccessPayload {
  business_id?: string;
  event_id?: string | null;
  id_channel?: number;
  created_by?: string;
  modified_by?: string | null;
  business_access_type_id?: number;
  start_date?: string;
  end_date?: string;
  business_access_status_id?: number;
  children_count?: number;
  qr_code?: boolean;
  is_analyzed?: boolean;
  main_guest?: {
    name: string;
    last_name: string;
    title_es?: string;
    tel: string;
  };
  business_guests?: Array<{
    name: string;
    last_name: string;
    title_es?: string;
  }>;
}

export const useCreateInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: InvitationPayload) => {
      const response = await directApiInstance({
        url: 'invitation',
        method: 'POST',
        data: payload
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      NotificationService.success("Invitation created successfully");
    },
    onError: (error: any) => {
      console.error('Error creating invitation:', error);
      NotificationService.error("Error creating pass");
    },
  });
};

/**
 * What the create call hands back. The response interceptor already unwrapped axios' `.data`, so `response` is the
 * body, `{ data: [row], message, ... }`; callers get its rows. A pass paid by link (SAM-1171) also answers
 * `payment_link` and `notifications` at the top level, once: then the whole body is kept so they are not lost.
 */
export const pickCreateResult = (response: any) => (response?.payment_link ? response : response?.data);

export const useCreateBusinessAccess = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BusinessAccessPayload) => {
      const response = await directApiInstance({
        url: '/business_access',
        method: 'POST',
        data: payload
      });
      return pickCreateResult(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-access-invitations'] });
      NotificationService.success("Pass created successfully");
    },
    onError: (error: any) => {
      console.error('Error creating business access:', error);
      NotificationService.error("Error creating business access");
    },
  });
};

// Combined hook that determines which endpoint to use
export const useCreateInvitationOrAccess = () => {
  const createInvitation = useCreateInvitation();
  const createBusinessAccess = useCreateBusinessAccess();

  const createInvitationOrAccess = async (data: any) => {
    const endpointType = sessionStorage.getItem('invitationEndpointType') || 'business_access';
    
    if (endpointType === 'invitation') {
      // Convert data to invitation format
      const statusId = data.is_analyzed ? "2" : "1";
      
      if (data.is_analyzed) {
      }
      
      const invitationPayload: InvitationPayload = {
        details: "",
        contact_id: "2", // TODO: Get from actual contact
        business_id: data.business_id,
        // Left out (not "undefined") when the form sent no type: the backend resolves the default.
        type_id: data.business_access_type_id == null ? undefined : String(data.business_access_type_id),
        invitation_from: data.start_date,
        invitation_to: data.end_date,
        status_id: statusId, // Use status_id = 2 for document scanning flow, 1 for share link flow
        children_count: String(data.children_count || 0),
        is_analyzed: data.is_analyzed, // Include is_analyzed for document scan flow
        main_guest: data.main_guest,
        companions: data.business_guests || []
      };

      // For residential invitations, always include estate_id (backend will handle validation)
      if (data.estate_id !== undefined) {
        invitationPayload.estate_id = String(data.estate_id);
      }
      
      return await createInvitation.mutateAsync(invitationPayload);
    } else {
      // Use business_access format - exclude estate_id for this endpoint, but include is_analyzed if present
      const { estate_id, ...businessAccessData } = data;
      // Always add qr_code: false for business access
      const businessAccessPayload = {
        ...businessAccessData,
        qr_code: false
      };
      
      if (businessAccessPayload.is_analyzed) {
      }
      
      return await createBusinessAccess.mutateAsync(businessAccessPayload);
    }
  };

  return {
    mutateAsync: createInvitationOrAccess,
    isPending: createInvitation.isPending || createBusinessAccess.isPending,
    error: createInvitation.error || createBusinessAccess.error
  };
}; 